import { z } from 'zod'
import { boardColumnSchema } from './board'
import { connectorIdSchema } from './connections'
import { calendarDate } from './dates'
import { aiModeSchema } from './env'
import {
  activeStatusSchema,
  boardLaneSchema,
  closedReasonSchema,
  itemKindSchema,
  itemOriginSchema,
  itemSourceSchema,
  itemStatusSchema,
  itemTypeSchema,
  suggestionConfidenceSchema,
  triageChoiceSchema,
  viaSchema,
} from './items'
import { outboxActionSchema, outboxStatusSchema, WITHDRAW_REASON_MAX } from './outbox'
import { headsUpEpisodeSchema } from './plan'
import { TIME_OFF_SUGGESTION_KEY_MAX } from './timeoff'

const iso = z.iso.datetime()
const count = z.number().int().min(0)
/** A time-off period that was added or removed: its days, never its label. */
const timeOffChange = z.object({
  timeOffId: z.uuid(),
  startsOn: calendarDate,
  endsOn: calendarDate,
})
/** An outbox entry's status before and after a transition (the audit trail of the gateway). */
const outboxTransition = {
  before: z.object({ status: outboxStatusSchema }),
  after: z.object({ status: outboxStatusSchema }),
}

/**
 * Every change in hugo is an event in the append-only `events` table: it is the audit log, the
 * change feed the clients follow (SSE), and the input from which insights are derived. Each
 * event type declares its data schema here. Item events carry enough state to rebuild an item's
 * history (see packages/insights derive.ts); they never carry message bodies from connectors.
 */
export const eventDataSchemas = {
  /** A session was created (sign-in). `via` is the auth endpoint path. */
  'session.started': z.object({ via: z.string() }),
  /** The user's test clock moved (test mode only). */
  'clock.changed': z.object({ offsetMs: z.number(), now: iso }),
  /** A test-mode ping used to verify live delivery end to end. */
  'debug.ping': z.object({ message: z.string().max(500) }),

  'item.created': z.object({
    title: z.string(),
    kind: itemKindSchema.nullable(),
    status: itemStatusSchema,
    origin: itemOriginSchema,
    source: itemSourceSchema,
    via: viaSchema,
    type: itemTypeSchema.nullable(),
    counterpartId: z.uuid().nullable(),
    dueAt: iso.nullable(),
    dueAllDay: z.boolean(),
    requestedAt: iso.nullable(),
    imported: z.boolean(),
  }),
  /** Filed from the inbox. `closedReason` is set for fyi (acknowledged) and ignore (ignored). */
  'item.triaged': z.object({
    choice: triageChoiceSchema,
    kind: itemKindSchema.nullable(),
    status: itemStatusSchema,
    closedReason: closedReasonSchema.nullable(),
  }),
  'item.kind_changed': z.object({ from: itemKindSchema.nullable(), to: itemKindSchema }),
  /** Plain field edits (title, notes, type, size, tags) with before/after values. */
  'item.updated': z.object({
    before: z.record(z.string(), z.unknown()),
    after: z.record(z.string(), z.unknown()),
  }),
  'item.due_set': z.object({ from: iso.nullable(), to: iso.nullable(), allDay: z.boolean() }),
  'item.counterpart_set': z.object({ from: z.uuid().nullable(), to: z.uuid().nullable() }),
  'item.snoozed': z.object({ until: iso, returnStatus: activeStatusSchema }),
  'item.woke': z.object({ status: activeStatusSchema }),
  'item.closed': z.object({ reason: closedReasonSchema }),
  'item.reopened': z.object({ status: activeStatusSchema }),
  /**
   * The user moved the item on the board: the column it was shown in (null: not on the board)
   * and the one it went to, with what hugo plans by now (lane, day or week, focus work).
   */
  'item.planned': z.object({
    from: boardColumnSchema.nullable(),
    to: boardColumnSchema,
    lane: boardLaneSchema.nullable(),
    date: calendarDate.nullable(),
    focus: z.boolean(),
  }),
  /** A new inbound signal (e.g. a message) on an existing item. No state change by itself. */
  'item.activity': z.object({ source: itemSourceSchema, occurredAt: iso }),
  /**
   * hugo's AI suggested where an inbox item belongs. No state change by itself, and no reason
   * text: the reason is written from the message and could quote it.
   */
  'item.suggested': z.object({
    choice: triageChoiceSchema,
    type: itemTypeSchema.nullable(),
    confidence: suggestionConfidenceSchema,
    model: z.string().max(200),
    mode: aiModeSchema,
  }),
  /** The user dismissed hugo's suggestion; the item stays where it is. */
  'item.suggestion_dismissed': z.object({}),

  'connection.connected': z.object({ connector: connectorIdSchema, accountEmail: z.string() }),
  'connection.disconnected': z.object({ connector: connectorIdSchema }),
  /** A sync or connect failed. `reason` is a short, token-free description. */
  'connection.failed': z.object({ connector: connectorIdSchema, reason: z.string().max(200) }),
  /** A sync step changed something: items created or updated, or busy time (calendars). */
  'connection.synced': z.object({
    connector: connectorIdSchema,
    created: count,
    updated: count,
    busy: count.default(0),
  }),

  /**
   * A calendar meeting no longer takes this time (cancelled, declined, moved or shortened), from
   * when the sync saw it on (decision 105). The title is the event's, as busy time keeps it.
   */
  'calendar.time_freed': z.object({
    externalId: z.string().max(500),
    title: z.string().max(500).nullable(),
    startsAt: z.iso.datetime(),
    endsAt: z.iso.datetime(),
  }),
  /** The user does not want to fill this freed time (by its key; nothing else). */
  'calendar.freed_time_dismissed': z.object({ key: z.string().max(600) }),

  /** Outbox events carry recipients and statuses, never the message body. */
  'outbox.drafted': z.object({
    outboxId: z.uuid(),
    connector: connectorIdSchema,
    action: outboxActionSchema,
    recipients: z.array(z.string()),
  }),
  /** Events recorded before the outbox module have no before/after. */
  'outbox.discarded': z.object({
    outboxId: z.uuid(),
    before: outboxTransition.before.optional(),
    after: outboxTransition.after.optional(),
  }),
  /**
   * hugo took back a pending heads-up or reminder that is no longer needed (the person replied,
   * the item was done or moved). `reason` is hugo's short text ("Marco replied"), no body.
   */
  'outbox.withdrawn': z.object({
    outboxId: z.uuid(),
    reason: z.string().max(WITHDRAW_REASON_MAX),
    ...outboxTransition,
  }),
  'outbox.sending': z.object({ outboxId: z.uuid(), ...outboxTransition }),
  'outbox.sent': z.object({
    outboxId: z.uuid(),
    connector: connectorIdSchema,
    externalId: z.string(),
    recipients: z.array(z.string()),
    ...outboxTransition,
  }),
  /** Approved while HUGO_WRITES_ENABLED is off: recorded, nothing left hugo. */
  'outbox.dry_run': z.object({
    outboxId: z.uuid(),
    recipients: z.array(z.string()),
    ...outboxTransition,
  }),
  'outbox.failed': z.object({
    outboxId: z.uuid(),
    error: z.string().max(300),
    ...outboxTransition,
  }),

  /** A time-off period was added or removed: the user's working time changed. */
  'time_off.added': timeOffChange,
  'time_off.removed': timeOffChange,
  /** The user does not want this out-of-office time as time off (no title, no dates). */
  'time_off.suggestion_dismissed': z.object({
    key: z.string().max(TIME_OFF_SUGGESTION_KEY_MAX),
  }),
  /** A heads-up was handled ("I told them"); the subject is its item. No text. */
  'headsup.dismissed': z.object({ episode: headsUpEpisodeSchema }),

  /** A token for a tool outside hugo (MCP) was made or revoked: its name, never the secret. */
  'access_token.created': z.object({ tokenId: z.uuid(), name: z.string() }),
  'access_token.revoked': z.object({ tokenId: z.uuid() }),

  'person.created': z.object({ name: z.string() }),
  /** An action was undone; its events no longer count (insights ignore them). */
  'action.undone': z.object({ actionId: z.uuid(), tool: z.string() }),
  'settings.changed': z.object({ fields: z.array(z.string()) }),
} as const

export type EventType = keyof typeof eventDataSchemas
export type EventData<T extends EventType> = z.output<(typeof eventDataSchemas)[T]>

/** Who caused an event. */
export const actorSchema = z.union([
  z.enum(['user', 'agent', 'system', 'test']),
  z.templateLiteral(['connector:', z.string()]),
])
export type Actor = z.output<typeof actorSchema>

/** The shape clients receive over SSE and from the feed endpoints. */
export const changeEventSchema = z.object({
  id: z.uuid(),
  seq: z.number().int().positive(),
  type: z.string(),
  actor: z.string(),
  subjectType: z.string().nullable(),
  subjectId: z.uuid().nullable(),
  actionId: z.uuid().nullable(),
  data: z.record(z.string(), z.unknown()),
  createdAt: z.iso.datetime(),
  occurredAt: z.iso.datetime(),
})
export type ChangeEvent = z.output<typeof changeEventSchema>

/** Validate event data against its declared schema before it is written. */
export function parseEventData<T extends EventType>(type: T, data: unknown): EventData<T> {
  return eventDataSchemas[type].parse(data) as EventData<T>
}

/** Item event types, i.e. the ones that change an item and feed insights. */
export type ItemEventType = Extract<EventType, `item.${string}`>
export function isItemEventType(type: string): type is ItemEventType {
  return type.startsWith('item.') && type in eventDataSchemas
}
